import { FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { useRetryableModuleImport } from '@glomopay/hooks';
import { Button } from '@glomopay/shadcn/button';

import { PaymentCheckoutContext } from '../contexts/payment-checkout.context';
import {
  CHECKOUT_DEPENDENCIES_FAILED_EVENT,
  CHECKOUT_EVENT_TO_OUTCOME,
  CHECKOUT_SDK_LABEL,
  CHECKOUT_SDK_ORIGIN,
  SUCCEEDED_CHECKOUT_EVENT_TO_OUTCOME_EVENT,
  TERMINAL_CHECKOUT_EVENTS,
} from '../constants/payment-session.constants';
import type {
  IGlomoCheckoutInstance,
  IGlomoCheckoutSdkModule,
  TCheckoutFlow,
  TCheckoutOutcome,
  TPaymentSession,
  TTerminalCheckoutEvent,
} from '../types/payment-session.types';

import { env } from '@/config/env';
import { analytics, AnalyticsEventName, CheckoutErrorCode, CloseTrigger, CtaName, PageName } from '@/features/analytics/public';
import { journeyId } from '@/lib/journey/journey-id';
import { logger } from '@/lib/logger';
import { LoadingScreen } from '@/shared/loading-screen/public';
import { StatusScreen } from '@/shared/status-screen/public';

type CheckoutPhase = 'idle' | 'loading' | 'open' | 'unavailable';

const trackCheckoutOutcome = (event: TTerminalCheckoutEvent, flow: TCheckoutFlow) => {
  if (event === 'payment.failure') {
    analytics.track(AnalyticsEventName.INVESTOR_CHECKOUT_FAILED, { flow, error_code: CheckoutErrorCode.PAYMENT_FAILED });
    return;
  }

  if (event === 'checkout.closed') {
    analytics.track(AnalyticsEventName.INVESTOR_CHECKOUT_CLOSED, { flow, trigger: CloseTrigger.USER_DISMISS });
    return;
  }

  analytics.track(AnalyticsEventName.INVESTOR_CHECKOUT_SUCCEEDED, { flow, outcome_event: SUCCEEDED_CHECKOUT_EVENT_TO_OUTCOME_EVENT[event] });
};

export const PaymentCheckoutProvider: FC<PropsWithChildren> = ({ children }) => {
  const [phase, setPhase] = useState<CheckoutPhase>('idle');

  const sessionRef = useRef<TPaymentSession | null>(null);
  const flowRef = useRef<TCheckoutFlow | null>(null);
  const settleRef = useRef<((outcome: TCheckoutOutcome) => void) | null>(null);
  const checkoutRef = useRef<IGlomoCheckoutInstance | null>(null);
  const unsubscribersRef = useRef<Array<() => void>>([]);

  const importCheckoutSdk = useRetryableModuleImport<IGlomoCheckoutSdkModule>(env.VITE_UNIFIED_SDK_URL, {
    label: CHECKOUT_SDK_LABEL,
    logger,
  });

  const settle = useCallback((outcome: TCheckoutOutcome) => {
    const resolveOutcome = settleRef.current;
    if (!resolveOutcome) return;
    settleRef.current = null;

    unsubscribersRef.current.forEach((unsubscribe) => unsubscribe());
    unsubscribersRef.current = [];
    checkoutRef.current?.close();
    checkoutRef.current = null;
    sessionRef.current = null;
    flowRef.current = null;
    analytics.setContext({ payment_session_id: undefined });

    setPhase('idle');
    resolveOutcome(outcome);
  }, []);

  const launch = useCallback(
    async (session: TPaymentSession, flow: TCheckoutFlow) => {
      setPhase('loading');

      const sdkModule = await importCheckoutSdk();
      if (!sdkModule) {
        logger.error('payment checkout sdk failed to load after all retries', { failureStage: 'import', sdkOrigin: CHECKOUT_SDK_ORIGIN });
        analytics.track(AnalyticsEventName.INVESTOR_CHECKOUT_FAILED, { flow, error_code: CheckoutErrorCode.SDK_IMPORT_FAILED });
        setPhase('unavailable');
        return;
      }

      try {
        // Investor portal only serves whitelabel tenants, so it takes the SDK's unbranded loading screen.
        const checkout = new sdkModule.CheckoutApi({ paymentSessionId: session.id, paymentSessionToken: session.token, _journeyId: journeyId });

        unsubscribersRef.current = [
          ...TERMINAL_CHECKOUT_EVENTS.map((event) =>
            checkout.on(event, () => {
              logger.info('payment checkout settled', { checkoutEvent: event });
              trackCheckoutOutcome(event, flow);
              settle({ status: CHECKOUT_EVENT_TO_OUTCOME[event] });
            }),
          ),
          checkout.on(CHECKOUT_DEPENDENCIES_FAILED_EVENT, () => {
            logger.error('payment checkout dependencies failed to load', { sdkOrigin: CHECKOUT_SDK_ORIGIN });
            analytics.track(AnalyticsEventName.INVESTOR_CHECKOUT_FAILED, { flow, error_code: CheckoutErrorCode.DEPENDENCIES_FAILED_TO_LOAD });
            settle({ status: 'unavailable' });
          }),
        ];

        checkoutRef.current = checkout;
        checkout.open();
        analytics.setContext({ payment_session_id: session.id });
        analytics.track(AnalyticsEventName.INVESTOR_PAGE_VIEWED, { page_name: PageName.CHECKOUT, flow });
        setPhase('open');
        logger.info('payment checkout opened', { paymentSessionId: session.id, checkoutFlow: flow });
      } catch (error) {
        logger.error('payment checkout sdk failed to initialize', {
          failureStage: 'construct',
          sdkOrigin: CHECKOUT_SDK_ORIGIN,
          error: error instanceof Error ? error.message : String(error),
        });
        analytics.track(AnalyticsEventName.INVESTOR_CHECKOUT_FAILED, { flow, error_code: CheckoutErrorCode.SDK_INIT_FAILED });
        setPhase('unavailable');
      }
    },
    [importCheckoutSdk, settle],
  );

  const openCheckout = useCallback(
    (session: TPaymentSession, flow: TCheckoutFlow) => {
      if (settleRef.current) {
        logger.error('payment checkout requested while one is already open', { paymentSessionId: session.id });
        return Promise.resolve<TCheckoutOutcome>({ status: 'unavailable' });
      }

      const outcome = new Promise<TCheckoutOutcome>((resolveOutcome) => {
        settleRef.current = resolveOutcome;
      });

      sessionRef.current = session;
      flowRef.current = flow;
      void launch(session, flow);

      return outcome;
    },
    [launch],
  );

  const value = useMemo(() => ({ openCheckout }), [openCheckout]);

  const retryLaunch = () => {
    analytics.track(AnalyticsEventName.INVESTOR_CTA_CLICKED, { cta_name: CtaName.RETRY_CHECKOUT, flow: flowRef.current ?? undefined });
    const session = sessionRef.current;
    if (!session || !flowRef.current) return settle({ status: 'unavailable' });
    void launch(session, flowRef.current);
  };

  const dismissUnavailable = () => {
    analytics.track(AnalyticsEventName.INVESTOR_CTA_CLICKED, { cta_name: CtaName.DISMISS_CHECKOUT, flow: flowRef.current ?? undefined });
    settle({ status: 'unavailable' });
  };

  return (
    <PaymentCheckoutContext.Provider value={value}>
      {children}

      {phase === 'loading' ? (
        <div className='fixed inset-0 z-50'>
          <LoadingScreen message='Opening your investment checkout' testId='checkout-loading-screen' />
        </div>
      ) : null}

      {phase === 'unavailable' ? (
        <div className='fixed inset-0 z-50'>
          <StatusScreen
            heading='We could not load the checkout'
            description='Please check your connection and try again. Your investment has not been started.'
            testId='checkout-unavailable-screen'
            headingTestId='checkout-unavailable-heading'
            action={
              <div className='flex gap-3'>
                <Button variant='brandFill' onClick={retryLaunch} data-testid='checkout-unavailable-retry-button'>
                  Try again
                </Button>
                <Button variant='secondary' onClick={dismissUnavailable} data-testid='checkout-unavailable-dismiss-button'>
                  Go back
                </Button>
              </div>
            }
          />
        </div>
      ) : null}
    </PaymentCheckoutContext.Provider>
  );
};
